import React, { useCallback, useEffect, useState } from 'react';
import ReactFlow, {
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Node,
  Edge,
  OnNodeDragStop,
  NodeProps,
  addEdge,
  Connection,
  OnConnect,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { supabase } from '@/lib/supabase';
import { Input } from '@/components/ui/input';
import { Play } from 'lucide-react';
import { getVideoUrl } from '@/lib/youtubeApi';

/* --------------- типы ---------------- */

interface PathNode {
  id: string;
  title: string;
  parent_id: string | null;
  collection_id: string | null;
  position_x: number | null;
  position_y: number | null;
  path_id?: string | null;
  type?: 'collection' | 'text';
}

interface Video {
  id: string;
  title: string;
  thumbnail: string;
  collection_id: string;
  video_id: string;
}

interface Collection {
  id: string;
  name: string;
  public_id?: string;
}

interface TextNodeData {
  title: string;
  isEditing?: boolean;
  onTitleChange?: (title: string) => void;
}

/* --------------- CollectionNode ---------------- */

function CollectionNode({ data, isConnectable }: NodeProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(data?.title ?? 'Untitled');
  const [hoveredVideo, setHoveredVideo] = useState<string | null>(null);

  const handleDoubleClick = (e: React.MouseEvent) => {
    if (data.onTitleChange) {
      e.stopPropagation();
      setIsEditing(true);
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (data.onTitleChange && title.trim() !== data.title) {
      data.onTitleChange(title.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBlur();
    }
  };

  const handleVideoClick = (videoId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(getVideoUrl(videoId), '_blank');
  };

  return (
    <div className="relative rounded border bg-zinc-800 text-white p-2 w-[320px]">
      {/* верхний приёмный */}
      <Handle
        type="target"
        id="top-target"
        position={Position.Top}
        style={{ background: '#555', zIndex: 10 }}
        isConnectable={isConnectable}
      />
      {/* верхний исходящий */}
      <Handle
        type="source"
        id="top-source"
        position={Position.Top}
        style={{ background: '#555', zIndex: 11 }}
        isConnectable={isConnectable}
      />

      <div className="flex items-center justify-between mb-1">
        <div className="font-bold text-sm truncate flex-1" onDoubleClick={handleDoubleClick}>
          {isEditing ? (
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              autoFocus
              className="bg-zinc-700 border-zinc-600 text-white"
            />
          ) : (
            data?.title ?? 'Untitled'
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-1">
        {(data?.videos ?? []).map((video: Video) => (
          <div
            key={video.id}
            className="relative"
            onMouseEnter={() => setHoveredVideo(video.id)}
            onMouseLeave={() => setHoveredVideo(null)}
          >
            <img
              src={video.thumbnail}
              alt={video.title}
              className="w-full h-20 object-cover rounded border cursor-pointer nodrag"
              onClick={(e) => handleVideoClick(video.video_id, e)}
              draggable={false}
            />
            {hoveredVideo === video.id && (
              <div
                className="absolute inset-0 flex items-center justify-center bg-black/50 rounded cursor-pointer nodrag"
                onClick={(e) => handleVideoClick(video.video_id, e)}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <Play className="w-8 h-8 text-white" />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* нижний исходящий */}
      <Handle
        type="source"
        id="bottom-source"
        position={Position.Bottom}
        style={{ background: '#555', zIndex: 10 }}
        isConnectable={isConnectable}
      />
    </div>
  );
}

/* --------------- TextNode ---------------- */

function TextNode({ data, isConnectable }: NodeProps<TextNodeData>) {
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(data?.title ?? 'Untitled');

  const handleDoubleClick = (e: React.MouseEvent) => {
    if (data.onTitleChange) {
      e.stopPropagation();
      setIsEditing(true);
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (data.onTitleChange && title.trim() !== data.title) {
      data.onTitleChange(title.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBlur();
    }
  };

  return (
    <div className="relative rounded border bg-yellow-100 text-black p-3 w-[300px] text-center font-semibold">
      {/* верхний приёмный */}
      <Handle
        type="target"
        id="top-target"
        position={Position.Top}
        style={{ background: '#555', zIndex: 10 }}
        isConnectable={isConnectable}
      />
      {/* верхний исходящий */}
      <Handle
        type="source"
        id="top-source"
        position={Position.Top}
        style={{ background: '#555', zIndex: 11 }}
        isConnectable={isConnectable}
      />

      {isEditing ? (
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          className="text-center font-semibold bg-yellow-100 border-yellow-200 focus-visible:ring-yellow-300"
        />
      ) : (
        <div onDoubleClick={handleDoubleClick}>{title}</div>
      )}

      {/* нижний исходящий */}
      <Handle
        type="source"
        id="bottom-source"
        position={Position.Bottom}
        style={{ background: '#555', zIndex: 10 }}
        isConnectable={isConnectable}
      />
    </div>
  );
}

/* --------------- остальной PathGraph без изменений ---------------- */

const nodeTypes = { collection: CollectionNode, text: TextNode };

const PathGraph = ({
  nodes,
  collectionVideos,
  selectedPathId,
  collections,
  isReadOnly = false,
}: {
  nodes: PathNode[];
  collectionVideos: Video[];
  selectedPathId: string | null;
  collections: Collection[];
  isReadOnly?: boolean;
}) => {
  /* ---- функции mapToFlowNodes / mapToEdges (без изменений) ---- */
  const mapToFlowNodes = (nodes: PathNode[]): Node[] =>
    nodes
      .map((node) => {
        const position = {
          x: node.position_x ?? Math.random() * 400,
          y: node.position_y ?? Math.random() * 400,
        };

        const foundCollection = collections.find((c) => c.id === node.collection_id);
        const nodeTitle = foundCollection?.name || node.title;
        const type = node.type === 'text' ? 'text' : 'collection';
        if (!nodeTypes[type]) return null;

        const videos = collectionVideos
          .filter((v) => v.collection_id === node.collection_id)
          .slice(0, 4);

        return {
          id: node.id,
          type,
          data: {
            title: nodeTitle,
            videos,
            collection: foundCollection,
            onTitleChange: !isReadOnly
              ? async (newTitle: string) => {
                  await supabase.from('path_nodes').update({ title: newTitle }).eq('id', node.id);
                }
              : undefined,
          },
          position,
        } as Node;
      })
      .filter(Boolean);

  const mapToEdges = (nodes: PathNode[]): Edge[] =>
    nodes
      .filter((n) => n.parent_id)
      .map((n) => ({
        id: `e${n.parent_id}-${n.id}`,
        source: n.parent_id!,
        target: n.id,
        type: 'smoothstep',
      }));

  const [flowNodes, setFlowNodes, onNodesChange] = useNodesState(mapToFlowNodes(nodes));
  const [flowEdges, setFlowEdges] = useEdgesState(mapToEdges(nodes));

  useEffect(() => {
    setFlowNodes(mapToFlowNodes(nodes));
    setFlowEdges(mapToEdges(nodes));
  }, [nodes, collectionVideos, collections]);

  const handleNodeDragStop: OnNodeDragStop = useCallback(
    async (_ev, node) => {
      if (isReadOnly) return;
      const { x, y } = node.position;
      await supabase.from('path_nodes').update({ position_x: x, position_y: y }).eq('id', node.id);
    },
    [isReadOnly],
  );

  const onConnect: OnConnect = useCallback(
    async (connection: Connection) => {
      if (isReadOnly) return;
      const { source, target } = connection;
      if (!source || !target) return;

      const src = flowNodes.find((n) => n.id === source);
      const dst = flowNodes.find((n) => n.id === target);
      if (!src || !dst) return;

      if (src.position.y <= dst.position.y) {
        await supabase.from('path_nodes').update({ parent_id: source }).eq('id', target);
        setFlowEdges((eds) => addEdge({ ...connection, type: 'smoothstep' }, eds));
      } else {
        console.warn('⛔ invalid upward connection');
      }
    },
    [flowNodes, isReadOnly, setFlowEdges],
  );

  /* ---- dnd drop / dragOver — без изменений ---- */

  const handleDrop = useCallback(
    async (ev: React.DragEvent) => {
      ev.preventDefault();
      if (isReadOnly || !selectedPathId) return;

      const collectionId = ev.dataTransfer.getData('collection_id');
      const collectionTitle = ev.dataTransfer.getData('collection_title');
      const isText = ev.dataTransfer.getData('text_block');

      const bounds = ev.currentTarget.getBoundingClientRect();
      const pos = { x: ev.clientX - bounds.left, y: ev.clientY - bounds.top };

      const { data: newNode } = await supabase
        .from('path_nodes')
        .insert([
          {
            path_id: selectedPathId,
            parent_id: null,
            collection_id: isText ? null : collectionId,
            title: isText || collectionTitle,
            type: isText ? 'text' : 'collection',
            position_x: pos.x,
            position_y: pos.y,
            level: 0,
            order_index: nodes.length,
          },
        ])
        .select()
        .single();

      const fn = mapToFlowNodes([newNode])[0];
      if (fn) setFlowNodes((nds) => [...nds, fn]);
    },
    [selectedPathId, isReadOnly, nodes.length],
  );

  const handleDragOver = useCallback((ev: React.DragEvent) => {
    ev.preventDefault();
    ev.dataTransfer.dropEffect = 'move';
  }, []);

  /* ---- render ---- */

  return (
    <div style={{ height: isReadOnly ? '100%' : 'calc(100vh - 200px)', minHeight: 500 }}>
      <ReactFlow
        nodes={flowNodes}
        edges={flowEdges}
        onNodesChange={isReadOnly ? undefined : onNodesChange}
        onNodeDragStop={isReadOnly ? undefined : handleNodeDragStop}
        onConnect={isReadOnly ? undefined : onConnect}
        nodeTypes={nodeTypes}
        nodesDraggable={!isReadOnly}
        nodesConnectable={!isReadOnly}
        elementsSelectable={!isReadOnly}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        fitView
        connectionMode="loose"
        defaultEdgeOptions={{ type: 'smoothstep' }}
      >
        <Background />
        <Controls showInteractive={!isReadOnly} />
      </ReactFlow>
    </div>
  );
};

export default PathGraph;